/* eslint-disable react/jsx-closing-tag-location */
/* eslint-disable max-len */
import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '../Icon';
import { toast } from '../Toast/ui/ToastContainer';
import { Typography } from '../Typography';
import styles from './Input.module.scss';
import type { InputProps } from './types';

const Input = ({
	labelPlaceholder,
	placeholder,
	initialValue = '',
	variant = 'default',
	onChange,
	onClear,
	onSave,
	className,
	maxLength,
}: InputProps) => {
	const [inputValue, setInputValue] = useState(initialValue);
	const [isEditing, setIsEditing] = useState(false);
	const [isFocused, setIsFocused] = useState(false);

	const inputReference = useRef<HTMLInputElement>(null);

	const isDisabled = variant === 'copy' || (variant === 'edit' && !isEditing);

	useEffect(() => {
		setInputValue(initialValue);
	}, [initialValue]);

	const handleCopy = (e: React.MouseEvent) => {
		e.stopPropagation();
		navigator.clipboard.writeText(String(initialValue));
		toast.info('Promocode Copied!');
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value;

		setInputValue(newValue);
		onChange?.(newValue);
	};

	const handleClear = (e: React.MouseEvent) => {
		e.stopPropagation();
		setInputValue('');
		onClear?.();
	};

	const handleEditToggle = (e: React.MouseEvent) => {
		e.stopPropagation();

		if (isEditing) {
			onSave?.(inputValue);
			setIsEditing(false);
		} else {
			setIsEditing(true);
		}
	};

	const handleWrapperClick = () => {
		inputReference.current?.focus();
		setTimeout(() => {
			inputReference.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
			});
		}, 300);
	};

	useEffect(() => {
		if (isEditing && inputReference.current) {
			inputReference.current.focus();
			inputReference.current.setSelectionRange(
				inputReference.current.value.length,
				inputReference.current.value.length,
			);
		}
	}, [isEditing]);

	return (
		<>
			<div
				className={`${styles.inputContainer} ${isFocused ? styles.focused : ''} ${className ?? ''}`}
				onClick={handleWrapperClick}
			>
				<div className={`${styles.inputWrapper} ${variant === 'search' ? styles.inputWrapperSearch : ''}`}>
					{labelPlaceholder && (
						<label>
							<Typography variant='footnote' color='lightblue-20' fontWeight={400}>
								{labelPlaceholder}
							</Typography>
						</label>
					)}
					{variant === 'search' && (
						<Icon name='sprite/search-icon' color='white' size={20} className={styles.searchIcon}></Icon>
					)}
					<input
						ref={inputReference}
						className={`${variant === 'search' ? styles.searchInput : ''}`}
						placeholder={placeholder}
						enterKeyHint='done'
						type='text'
						autoCorrect='off'
						autoCapitalize='none'
						disabled={isDisabled}
						value={inputValue}
						maxLength={maxLength || 256}
						onChange={handleChange}
						onFocus={() => {
							setIsFocused(true);
						}}
						onBlur={() => {
							setIsFocused(false);
						}}
					/>
				</div>
				{variant === 'copy' && (
					<button className={styles.iconButton} onClick={handleCopy}>
						<Icon name='sprite/copy-icon' size={20} color='lightblue-40'></Icon>
					</button>
				)}
				{variant === 'edit' && (
					<button
						className={styles.iconButton}
						onClick={(e) => {
							handleEditToggle(e);

							if (isEditing) {
								onSave?.(inputValue);
							}
						}}
					>
						<Icon name={isEditing ? 'sprite/mark-icon' : 'sprite/edit-pencil-icon'} size={25} color='lightblue-40' />
					</button>
				)}
				{variant === 'search' && (
					<button className={styles.iconButton} onClick={handleClear}>
						<Icon name='sprite/dismiss-icon' color='lightblue-40' size={20}></Icon>
					</button>
				)}
			</div>
		</>
	);
};

export default Input;
